import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { type Transaction, useProjectMutations } from '@/modules/projects'
import type { TransactionsTableVariant } from '@/modules/projects/components/tables/transactions/columns'
import type { Check } from '@/modules/projects/stores'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'

const dialogId = 'delete-transactions'

// Caller passes either a bare list (legacy single-transaction dropdown path)
// or {transactions, variant} (the bulk-actions toolbar). Normalize both into
// a typed shape so the dialog can route to the right mutation.
interface DialogPayload {
  transactions?: Transaction[] | Check[]
  variant?: TransactionsTableVariant
}

const isPayload = (data: unknown): data is DialogPayload =>
  !!data &&
  typeof data === 'object' &&
  !Array.isArray(data) &&
  'transactions' in (data as object)

export const DeleteTransactionsDialog = () => {
  const { successToast, errorToast } = useToast()
  const open = useDialogIsOpen(dialogId)
  const raw = useDialogData()
  const payload: DialogPayload = isPayload(raw)
    ? raw
    : { transactions: (raw as Transaction[] | undefined) || [] }
  const transactionsToDelete = payload.transactions || []
  // Prefer the explicit variant (bulk-actions toolbar passes it). Fall back
  // to row-level detection (per-row context menu passes a bare array): a check
  // row carries `typename === "CheckRecord"`.
  const detectedFromRow =
    transactionsToDelete[0]?.typename === 'CheckRecord' ? 'checks' : undefined
  const variant: TransactionsTableVariant =
    payload.variant ?? detectedFromRow ?? 'transactions'
  const isChecks = variant === 'checks'
  const { closeDialog } = useDialogActions()
  const total = transactionsToDelete?.length || 0
  const single = total === 1 ? transactionsToDelete[0] : null
  const noun = isChecks
    ? single
      ? 'Check'
      : 'Checks'
    : single
      ? 'Transaction'
      : 'Transactions'

  const { deleteTransactions, deleteChecks } = useProjectMutations()
  const handleSubmit = () => {
    const promise = isChecks
      ? deleteChecks(transactionsToDelete as Check[])
      : deleteTransactions(transactionsToDelete as Transaction[])

    promise
      .then(() => successToast(`${noun} deleted successfully`))
      .catch((error) =>
        errorToast(`Failed to delete ${noun.toLowerCase()}`, error),
      )
    closeDialog(true)
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {noun}?</DialogTitle>
          <DialogDescription>
            This will permanently delete{' '}
            {single
              ? `this ${noun.toLowerCase()}`
              : `${total} ${noun.toLowerCase()}`}
            . This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-4">
          <Button theme="outline" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} theme="danger">
            Delete {noun}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

DeleteTransactionsDialog.id = dialogId
